/**
 * Static constants and lookup tables. Not user-editable.
 */

export { COUNTRY_TIMEZONE, timezoneFor } from './country-timezones.js';
export {
  COUNTRIES_ALL,
  COUNTRY_BY_ISO3,
  countryName,
  type CountryEntry,
} from './countries.js';

/** All 37 launch markets (ISO-3166 alpha-3). Single source of truth — services
 *  and clients use this list for country pickers, validation, and per-market
 *  configuration. Excludes Iran (OFAC). */
export const LAUNCH_COUNTRIES_ISO3 = [
  // North America
  'USA',
  'CAN',
  // Europe (English-speaking + EU)
  'GBR',
  'DEU',
  'FRA',
  // Oceania
  'AUS',
  'NZL',
  // South Asia
  'PAK',
  'IND',
  'BGD',
  // Anatolia & Balkans
  'TUR',
  'BIH',
  'ALB',
  'XKX', // Kosovo (non-standard but widely used)
  // MENA / GCC
  'ARE',
  'SAU',
  'EGY',
  'MAR',
  'JOR',
  'LBN',
  'IRQ',
  'QAT',
  'KWT',
  'BHR',
  'OMN',
  'TUN',
  'DZA',
  'SDN',
  // Central Asia
  'AFG',
  // Sub-Saharan Africa
  'NGA',
  'SEN',
  'MLI',
  'NER',
  // Southeast Asia
  'IDN',
  'MYS',
  'SGP',
  'BRN',
] as const;

export type LaunchCountry = (typeof LAUNCH_COUNTRIES_ISO3)[number];

/** Currency used for billing in each launch country. Some countries default
 *  to USD where their local currency has limited Stripe support or is in
 *  severe inflationary distress. */
export const COUNTRY_BILLING_CURRENCY: Record<LaunchCountry, string> = {
  USA: 'USD',
  CAN: 'CAD',
  GBR: 'GBP',
  DEU: 'EUR',
  FRA: 'EUR',
  AUS: 'AUD',
  NZL: 'NZD',
  PAK: 'PKR',
  IND: 'INR',
  BGD: 'BDT',
  TUR: 'TRY',
  BIH: 'BAM',
  ALB: 'ALL',
  XKX: 'EUR',
  ARE: 'AED',
  SAU: 'SAR',
  EGY: 'EGP',
  MAR: 'MAD',
  JOR: 'JOD',
  LBN: 'USD', // currency crisis
  IRQ: 'USD', // limited Stripe support
  QAT: 'QAR',
  KWT: 'KWD',
  BHR: 'BHD',
  OMN: 'OMR',
  TUN: 'TND',
  DZA: 'DZD',
  SDN: 'USD', // sanctions / banking
  AFG: 'USD', // no AFN support in Stripe
  NGA: 'NGN',
  SEN: 'XOF',
  MLI: 'XOF',
  NER: 'XOF',
  IDN: 'IDR',
  MYS: 'MYR',
  SGP: 'SGD',
  BRN: 'BND',
};

/** Default search radius in km by country. Tuned for major-metro density. */
export const DEFAULT_SEARCH_RADIUS_KM: Record<LaunchCountry, number> = {
  // Dense / metro-heavy
  USA: 50,
  CAN: 100,
  GBR: 50,
  DEU: 80,
  FRA: 80,
  AUS: 100,
  NZL: 200,
  ARE: 50,
  SAU: 80,
  QAT: 50,
  KWT: 50,
  BHR: 30,
  OMN: 80,
  SGP: 30,
  BRN: 50,
  // Diaspora / mid-density
  TUR: 80,
  EGY: 80,
  MAR: 100,
  JOR: 60,
  LBN: 50,
  TUN: 80,
  DZA: 100,
  BIH: 80,
  ALB: 80,
  XKX: 60,
  MYS: 80,
  IDN: 80,
  // Spread / lower-density
  PAK: 100,
  IND: 100,
  BGD: 100,
  IRQ: 100,
  SDN: 200,
  NGA: 100,
  SEN: 200,
  MLI: 250,
  NER: 250,
  AFG: 200,
};
